// The PDF document itself — a dedicated A4 print layout, not a capture of the
// page. Page 1: branded header + key metrics + chart shots. Page 2+: the
// deepdive table as native text rows (header repeats per page via `fixed`).
// White-label swaps the ClickGuard logo for the uploaded logo (brand-name
// text when no logo), tints the accent rule with the brand color, and keeps
// ClickGuard out of every string: no byline, no footer attribution, neutral
// "the system" voice in the summary/glossary/methodology.
import {
	Document,
	Font,
	Image,
	Page,
	StyleSheet,
	Text,
	View
} from '@react-pdf/renderer'
import noahRegular from 'assets/fonts/noah/Noah-Regular.otf'
import noahMedium from 'assets/fonts/noah/Noah-Medium.otf'
import noahBold from 'assets/fonts/noah/Noah-Bold.otf'
import type { ReportPdfData } from './build-report-data'

Font.register({
	family: 'noah',
	fonts: [
		{ src: noahRegular },
		{ src: noahMedium, fontWeight: 500 },
		{ src: noahBold, fontWeight: 700 }
	]
})
// Print copy shouldn't hyphenate metric labels / dimension values.
Font.registerHyphenationCallback((word) => [word])

const NAVY = '#171053'

// Highlights section switch — hidden for now (Esteban 2026-07-22); the findings
// data pipeline still runs, this only skips rendering the card.
const SHOW_HIGHLIGHTS = false
const INK = '#535255'
const MUTED = '#9f9ea3'
const BORDER = '#e1e1e1'
const ZEBRA = '#f7f7f8'
const GREEN = '#12b76a'
const RED = '#f04438'

const styles = StyleSheet.create({
	page: {
		fontFamily: 'noah',
		fontSize: 10,
		color: INK,
		paddingTop: 40,
		paddingHorizontal: 40,
		paddingBottom: 56
	},
	brandRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: 24
	},
	wordmark: { fontSize: 16, fontWeight: 700, color: NAVY },
	logo: { height: 28, objectFit: 'contain', alignSelf: 'flex-start' },
	headerRight: { alignItems: 'flex-end' },
	generated: { fontSize: 9, color: MUTED },
	preparedBy: { fontSize: 9, color: MUTED, marginTop: 2 },
	title: { fontSize: 22, fontWeight: 500, color: NAVY },
	dateRange: { fontSize: 11, color: INK, marginTop: 4 },
	accent: { width: 48, height: 3, borderRadius: 2, marginTop: 10 },
	scopeLine: { fontSize: 9.5, color: INK, marginTop: 12 },
	summary: { fontSize: 10.5, color: INK, lineHeight: 1.5, marginTop: 6, maxWidth: '90%' },
	findingsCard: {
		borderWidth: 1,
		borderColor: BORDER,
		borderRadius: 8,
		padding: 12,
		marginTop: 16
	},
	findingsHeading: { fontSize: 11, fontWeight: 500, color: NAVY, marginBottom: 8 },
	findingRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 6 },
	findingBullet: { width: 3, height: 3, marginTop: 4, marginRight: 6 },
	findingText: { fontSize: 9.5, color: INK, flex: 1 },
	section: { fontSize: 13, fontWeight: 500, color: NAVY, marginTop: 24, marginBottom: 10 },
	tiles: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
	tile: {
		width: '32%',
		borderWidth: 1,
		borderColor: BORDER,
		borderRadius: 8,
		padding: 10
	},
	tileLabel: { fontSize: 8.5, color: INK },
	tileValue: { fontSize: 16, fontWeight: 500, color: NAVY, marginTop: 4 },
	tileChange: { fontSize: 8.5, marginTop: 3 },
	// Full-width, one per row; the section flows across pages, which
	// wrap={false} per block handles cleanly.
	charts: { flexDirection: 'column', gap: 14 },
	// Panel titles print as real text above each card — the in-shot header is
	// hidden during capture (data-pdf-hide, customize.css) so the title isn't
	// doubled and sits flush left instead of inheriting the panel's padding.
	chartTitle: { fontSize: 11, fontWeight: 500, color: NAVY, marginBottom: 5 },
	// The shot already carries the panel's own 16px padding, so the block adds
	// only a hairline inset inside its border (was 8pt — read as double padding).
	chartBlock: {
		width: '100%',
		borderWidth: 1,
		borderColor: BORDER,
		borderRadius: 8,
		padding: 3
	},
	chartImg: { width: '100%' },
	chartMissing: { padding: 16 },
	chartMissingTitle: { fontSize: 11, fontWeight: 500, color: NAVY },
	chartMissingNote: { fontSize: 9, color: MUTED, marginTop: 4 },
	tableTitle: { fontSize: 13, fontWeight: 500, color: NAVY, marginBottom: 10 },
	tr: { flexDirection: 'row', alignItems: 'center' },
	th: {
		flexDirection: 'row',
		alignItems: 'center',
		borderBottomWidth: 1,
		borderBottomColor: BORDER,
		paddingBottom: 6,
		marginBottom: 2
	},
	thText: { fontSize: 8.5, fontWeight: 500, color: MUTED },
	cellLabel: { flex: 2, fontSize: 9.5, color: NAVY, paddingRight: 8 },
	cell: { flex: 1, fontSize: 9.5, textAlign: 'right' },
	rowPad: { paddingVertical: 5, paddingHorizontal: 6, borderRadius: 4 },
	glossaryRow: { marginTop: 6 },
	glossaryTerm: { fontSize: 9.5, fontWeight: 500, color: NAVY },
	glossaryDef: { fontSize: 9, color: INK, marginTop: 2 },
	methodology: { fontSize: 10, color: INK, lineHeight: 1.5, marginTop: 8 },
	emptyNote: { fontSize: 9.5, color: MUTED, marginTop: 8 },
	footer: {
		position: 'absolute',
		bottom: 24,
		left: 40,
		right: 40,
		flexDirection: 'row',
		justifyContent: 'space-between',
		fontSize: 8,
		color: MUTED
	}
})

const Footer = ({ attribution }: { attribution: boolean }) => (
	<View style={styles.footer} fixed>
		<Text>{attribution ? 'Generated by ClickGuard Protection Analytics' : ' '}</Text>
		<Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
	</View>
)

const Change = ({ m }: { m: ReportPdfData['metrics'][number] }) => {
	const match = m.change.replace(/,/g, '').match(/[+-]?(?:\d+(?:\.\d*)?|\.\d+)/)
	const magnitude = match ? Math.abs(Number(match[0])) : NaN
	if (!m.change.trim()) return null
	if (/^no change$/i.test(m.change.trim()) || magnitude === 0) {
		return <Text style={[styles.tileChange, { color: MUTED }]}>No change vs previous period</Text>
	}
	const color = m.trend === 'positive' ? GREEN : m.trend === 'negative' ? RED : MUTED
	const arrow = m.direction === 'up' ? '↑ ' : m.direction === 'down' ? '↓ ' : ''
	return (
		<Text style={[styles.tileChange, { color }]}>
			{arrow}
			{m.change} vs previous period
		</Text>
	)
}

export const ReportPdf = ({ data }: { data: ReportPdfData }) => {
	// The header logo: the customer's upload when white-labelled, ClickGuard's
	// full logo (logomark + wordmark) otherwise — both arrive pre-rasterized as
	// `logoDataUri` (see pdf-logo.ts).
	const brandHeader = data.logoDataUri ? (
		<Image style={styles.logo} src={data.logoDataUri} />
	) : data.whiteLabel ? (
		// White-label requires a logo (the drawer gates download on it), so this
		// blank header only shows in the edge case where the upload can't be
		// rasterized (e.g. an unparseable file) and logoDataUri degrades to null.
		<Text> </Text>
	) : (
		// Fallback only: the bundled ClickGuard logo could not be rasterized.
		<Text style={styles.wordmark}>ClickGuard</Text>
	)
	return (
		<Document title={data.title} author={data.whiteLabel ? undefined : 'ClickGuard'}>
			<Page size="A4" style={styles.page}>
				<View style={styles.brandRow}>
					{brandHeader}
					<View style={styles.headerRight}>
						<Text style={styles.generated}>Generated {data.generatedAt}</Text>
						{data.preparedBy !== null && (
							<Text style={styles.preparedBy}>Prepared by {data.preparedBy}</Text>
						)}
					</View>
				</View>
				<Text style={styles.title}>{data.title}</Text>
				<Text style={styles.dateRange}>{data.dateRange}</Text>
				<View style={[styles.accent, { backgroundColor: data.brandColor }]} />
				<Text style={styles.scopeLine}>{data.scopeLine}</Text>
				<Text style={styles.summary}>{data.summary}</Text>

				{/* Highlights hidden for now (Esteban 2026-07-22). Render-level switch
				    only — the findings pipeline in build-report-data.ts still runs and
				    stays tested; flip SHOW_HIGHLIGHTS to bring the section back. */}
				{SHOW_HIGHLIGHTS && data.findings.length > 0 && (
					<View style={styles.findingsCard} wrap={false}>
						<Text style={styles.findingsHeading}>Highlights</Text>
						{data.findings.map((f, i) => (
							<View key={i} style={styles.findingRow}>
								<View style={[styles.findingBullet, { backgroundColor: data.brandColor }]} />
								<Text style={styles.findingText}>{f}</Text>
							</View>
						))}
					</View>
				)}

				{data.metrics.length > 0 && (
					<>
						<Text style={styles.section}>{data.metricSection}</Text>
						<View style={styles.tiles}>
							{data.metrics.map((m) => (
								<View key={m.label} style={styles.tile} wrap={false}>
									<Text style={styles.tileLabel}>{m.label}</Text>
									<Text style={styles.tileValue}>{m.value}</Text>
									<Change m={m} />
								</View>
							))}
						</View>
					</>
				)}

				{data.charts.length > 0 && (
					<>
						{/* minPresenceAhead: if less than ~one chart block (~300pt)
						    fits below, push the heading to the next page rather than
						    orphaning it at the bottom. */}
						<Text style={styles.section} minPresenceAhead={300}>
							{data.chartSection}
						</Text>
						<View style={styles.charts}>
							{data.charts.map((c) =>
								c.png ? (
									<View key={c.id} wrap={false}>
										<Text style={styles.chartTitle}>{c.title}</Text>
										<View style={styles.chartBlock}>
											<Image style={styles.chartImg} src={c.png} />
										</View>
									</View>
								) : (
									<View key={c.id} style={[styles.chartBlock, styles.chartMissing]} wrap={false}>
										<Text style={styles.chartMissingTitle}>{c.title}</Text>
										<Text style={styles.chartMissingNote}>Chart unavailable</Text>
									</View>
								)
							)}
						</View>
					</>
				)}
				<Footer attribution={!data.whiteLabel} />
			</Page>

			<Page size="A4" style={styles.page}>
				<Text style={styles.tableTitle}>Breakdown by {data.table.dimensionLabel}</Text>
				{/* columns[0] is the dimension label column (both payload branches
				    ship it), aligned 1:1 with each row's cells. */}
				<View style={styles.th} fixed>
					<Text style={[styles.cellLabel, styles.thText]}>{data.table.columns[0]}</Text>
					{data.table.columns.slice(1).map((c) => (
						<Text key={c} style={[styles.cell, styles.thText]}>
							{c}
						</Text>
					))}
				</View>
				{data.table.rows.length > 0 ? (
					data.table.rows.map((r, i) => (
						<View
							key={`${i}-${r.cells[0]}`}
							style={[styles.tr, styles.rowPad, i % 2 ? { backgroundColor: ZEBRA } : {}]}
							wrap={false}
						>
							<Text style={[styles.cellLabel, { paddingLeft: r.depth * 12 }]}>{r.cells[0]}</Text>
							{r.cells.slice(1).map((cell, ci) => (
								<Text key={ci} style={styles.cell}>
									{cell}
								</Text>
							))}
						</View>
					))
				) : (
					<Text style={styles.emptyNote}>No breakdown data is available for this report.</Text>
				)}
				<Footer attribution={!data.whiteLabel} />
			</Page>

			<Page size="A4" style={styles.page}>
				<Text style={styles.title}>About this report</Text>
				{data.glossary.length > 0 && (
					<>
						<Text style={styles.section}>Glossary</Text>
						{data.glossary.map((g) => (
							<View key={g.term} style={styles.glossaryRow}>
								<Text style={styles.glossaryTerm}>{g.term}</Text>
								<Text style={styles.glossaryDef}>{g.definition}</Text>
							</View>
						))}
					</>
				)}
				<Text style={styles.section}>Methodology</Text>
				{/* White-label drops the vendor as sentence subject; the rest of the
				    paragraph is already passive so only the opener differs. */}
				<Text style={styles.methodology}>
					{data.whiteLabel
						? 'Every ad click is analyzed in real time using device, network, and behavior signals. '
						: 'ClickGuard analyzes every ad click in real time using device, network, and behavior signals. '}
					Clicks that match known fraud patterns are classified as invalid, and the
					sources behind them are blocked from seeing your ads again. Clicks that show
					risk signals are marked suspicious, and their sources stay under review until
					cleared or confirmed. Figures in this report reflect the data visible on the
					report at download time.
				</Text>
				<Footer attribution={!data.whiteLabel} />
			</Page>
		</Document>
	)
}
